import { parsePercent } from 'util/NumberUtils'
import { parseSharesVolume, parseValuationSharePrice } from 'util/ParsersFormatters'

import { SharePriceRecordedAsType, TradeIntentType } from 'graphql/bindings/AdminBindings'
import numeral from 'numeral'

export const disableEnterKeySubmission = (e: React.KeyboardEvent<HTMLFormElement>) => {
  if (e.key === 'Enter') e.preventDefault()
}

export const validateSizeOrSharesField = (input?: string) => {
  const parsedSizeOrShares = parseSharesVolume(input ?? '')

  if (input && parsedSizeOrShares.isError) {
    return 'This format is invalid'
  }

  if (!parsedSizeOrShares.isError && parsedSizeOrShares.isRange) {
    if (parsedSizeOrShares.value.min > parsedSizeOrShares.value.max) {
      return 'This is invalid - cannot submit an inverted range'
    }
  }

  if (!parsedSizeOrShares.isError && parsedSizeOrShares.value > 1000000000) {
    return 'This is invalid - one cannot store such a gargantuan opportunity'
  }
}

export const validatePriceOrValuation = (input?: string, type?: TradeIntentType) => {
  const parsedPriceOrValuation = parseValuationSharePrice(input ?? '')

  if (input && parsedPriceOrValuation.isError) {
    return 'This format is invalid'
  }

  if (input && !parsedPriceOrValuation.isError) {
    if (type === TradeIntentType.Ask && parsedPriceOrValuation.isNtb) {
      return 'Cannot specify a NTB price on an ask'
    } else if (type === TradeIntentType.Bid && parsedPriceOrValuation.isNts) {
      return 'Cannot specify a NTS price on an bid'
    }

    if (parsedPriceOrValuation.value === 0) {
      return 'Cannot specify a zero price'
    }
  }
}

export const validateFeeField = (fee?: string) => {
  if (fee === undefined) return
  const parsedFee = parsePercent(fee)
  if (parsedFee && (parsedFee > 100 || parsedFee < 0)) {
    return 'Fee is invalid'
  }
}

export const getPriceOrValuation = (
  pricing_type: string | null | undefined,
  share_price: any,
  valuation: any,
  fee: number,
  share_price_recorded_as: SharePriceRecordedAsType
) => {
  if (pricing_type === 'share_price') {
    switch (share_price_recorded_as) {
      case SharePriceRecordedAsType.Ntb:
        const ntbPrice = Number(share_price) * (1 + fee)
        return `${numeral(ntbPrice)
          .format('($0[.]00a)')
          .toUpperCase()} NTB pps`
      case SharePriceRecordedAsType.Nts:
        const ntsPrice = Number(share_price) * (1 - fee)
        return `${numeral(ntsPrice)
          .format('($0[.]00a)')
          .toUpperCase()} NTS pps`
      default:
        return `${numeral(share_price)
          .format('($0[.]00a)')
          .toUpperCase()} gross pps`
    }
  }
  if (pricing_type === 'valuation') {
    return `${numeral(valuation)
      .format('($0[.]000a)')
      .toUpperCase()} val`
  }
}

export const parseSizeOrPrice = (unparsed: string, type: 'price' | 'size', fee: number | undefined) => {
  if (type === 'size') {
    const parsedSizeOrShares = parseSharesVolume(unparsed)
    if (parsedSizeOrShares.isError) {
      return
    }

    const base = {
      min_dollar_amount: null,
      max_dollar_amount: null,
      min_num_shares: null,
      max_num_shares: null,
    }

    if (parsedSizeOrShares.type === 'shares') {
      if (parsedSizeOrShares.isRange) {
        return { ...base, min_num_shares: parsedSizeOrShares.value.min, max_num_shares: parsedSizeOrShares.value.max }
      }
      return { ...base, min_num_shares: parsedSizeOrShares.value, max_num_shares: parsedSizeOrShares.value }
    }
    if (parsedSizeOrShares.isRange) {
      return {
        ...base,
        min_dollar_amount: parsedSizeOrShares.value.min,
        max_dollar_amount: parsedSizeOrShares.value.max,
      }
    }
    return { ...base, min_dollar_amount: parsedSizeOrShares.value, max_dollar_amount: parsedSizeOrShares.value }
  }
  const parsedPriceOrValuation = parseValuationSharePrice(unparsed)
  if (parsedPriceOrValuation.isError) {
    return
  }

  const base = {
    share_price: null,
    valuation: null,
  }

  if (parsedPriceOrValuation.type === 'price') {
    if (parsedPriceOrValuation.isNtb) {
      return {
        ...base,
        share_price: fee !== 0 ? parsedPriceOrValuation.value / (1 + (fee ?? 0)) : parsedPriceOrValuation.value,
        share_price_recorded_as: SharePriceRecordedAsType.Ntb,
      }
    } else if (parsedPriceOrValuation.isNts) {
      return {
        ...base,
        share_price: fee !== 0 ? parsedPriceOrValuation.value / (1 - (fee ?? 0)) : parsedPriceOrValuation.value,
        share_price_recorded_as: SharePriceRecordedAsType.Nts,
      }
    }
    return {
      ...base,
      share_price: parsedPriceOrValuation.value,
      share_price_recorded_as: SharePriceRecordedAsType.Gross,
    }
  }
  return { ...base, valuation: parsedPriceOrValuation.value }
}
